<template>
  <!-- Pure layer-tree content; the surrounding drawer/tabs live in RightPanel.vue -->
  <n-spin :show="loading">
    <div v-if="error" class="error-msg">{{ error }}</div>

    <div v-else-if="!rootGroup || rootGroup.type !== 'group' || !rootGroup.children.length" class="empty-msg">
      Ehhez a gazdasághoz nem található réteg.
    </div>

    <div v-else class="tab-content">
      <!-- Single root group: its name is not shown; render its children directly. -->
      <n-collapse :default-expanded-names="rootGroup.children.map(i => String(i.id))">
        <n-collapse-item
          v-for="child in rootGroup.children"
          :key="child.id"
          :title="child.title"
          :name="String(child.id)"
          :class="{ 'group-item--hidden': child.type === 'group' && hiddenGroupNodeIds.has(child.id) }"
        >
          <template v-if="child.type === 'group'" #header-extra>
            <button
              class="eye-btn"
              :title="hiddenGroupNodeIds.has(child.id) ? 'Show on map' : 'Hide on map'"
              @click.stop="toggleGroupVisibility(child.id)"
            >
              <svg v-if="!hiddenGroupNodeIds.has(child.id)" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
              <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                <line x1="1" y1="1" x2="23" y2="23"/>
              </svg>
            </button>
          </template>
          <!-- Depth-1 group: childScope() applies the child group's own
               exclusive_selection and decides whether it joins the root scope -->
          <LayerNodeChildren
            v-if="child.type === 'group' && rootScopeValue"
            :nodes="child.children"
            :depth="2"
            :scope="childScope(rootScopeValue, child)"
          />
          <!-- Depth-1 leaf nodes sit directly in the root group's scope -->
          <template v-else-if="rootScopeValue">
            <RasterTypeControl
              v-if="child.type === 'raster_type'"
              :node="child"
              :scope="rootScopeValue"
            />
            <RasterLayerControl
              v-else-if="child.type === 'raster_layer'"
              :node="child"
              :scope="rootScopeValue"
            />
            <VectorLayerControl
              v-else-if="child.type === 'vector'"
              :node="child"
              :scope="rootScopeValue"
            />
          </template>
        </n-collapse-item>
      </n-collapse>
    </div>
  </n-spin>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { NCollapse, NCollapseItem, NSpin } from 'naive-ui'
import type { LayerTreeNode } from '@/types/layerTree'
import { useLayerSelection } from '@/composables/useLayerSelection'
import { rootScope, childScope } from '@/composables/layerScope'
import LayerNodeChildren from './LayerNodeChildren.vue'
import RasterTypeControl from './RasterTypeControl.vue'
import RasterLayerControl from './RasterLayerControl.vue'
import VectorLayerControl from './VectorLayerControl.vue'

const props = defineProps<{
  layerTree: LayerTreeNode[]
  loading: boolean
  error: string | null
}>()

const { hiddenGroupNodeIds, toggleGroupVisibility } = useLayerSelection()

// The farm exposes a single root group; render its children directly (no tabs).
const rootGroup = computed(() => props.layerTree[0] ?? null)

// Selection scope the root group's children live in. Sub-groups derive theirs
// from this via childScope(), which honours each group's exclusive_selection.
const rootScopeValue = computed(() =>
  rootGroup.value?.type === 'group' ? rootScope(rootGroup.value) : null,
)
</script>

<style scoped>
.tab-content {
  padding: 4px 2px 4px;
}

.error-msg { color: #f38ba8; font-size: 12px; padding: 8px 4px; }
.empty-msg { font-size: 12px; color: #585b70; padding: 24px 8px; text-align: center; }

/* ── Group eye toggle ───────────────────────────────────────────────────── */
.eye-btn {
  background: none;
  border: none;
  padding: 2px 4px;
  cursor: pointer;
  color: #6c7086;
  display: flex;
  align-items: center;
  border-radius: 3px;
  transition: color 0.12s;
}
.eye-btn:hover { color: #89b4fa; }
:deep(.group-item--hidden) { opacity: 0.45; }
:deep(.group-item--hidden .n-collapse-item__header-main) { --n-title-text-color: #6c7086 !important; }

/* ── Naive UI overrides ─────────────────────────────────────────────────── */
:deep(.n-collapse-item__header-main) { --n-title-text-color: #cdd6f4 !important; }
:deep(.n-base-icon) { color: #a6e3a1 !important; }
</style>
